import React, { useEffect, useState, useMemo } from "react";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { useNavigate } from "react-router-dom";
import { HiOutlinePlus } from "react-icons/hi";
import { AiOutlineEdit } from "react-icons/ai";
import { toast } from "react-hot-toast";
import { useGetWheelCampaignListMutation } from "../../../features/tenant/wheelCampaign/wheelCampaignApi";
import { getVendorName } from "../../../utils/getVendorName";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import {
  FaEye,
  FaPlus,
  FaChartLine,
  FaCheckCircle,
  FaPauseCircle,
  FaTrash,
  FaTimes,
  FaGift,
  FaCalendarAlt,
  FaWallet,
  FaListAlt
} from "react-icons/fa";
import { Modal, Backdrop, Fade, Box } from "@mui/material";
import Wheels from "../../../components/Tenant/Wheel/Wheel";
import { useGetWheelByCampaignIdMutation } from "../../../features/tenant/wheel/wheelApi";
import { useGetWheelRewardsByWheelIdMutation } from "../../../features/tenant/wheelRewards/wheelRewardsApi";
import "./Wheel.css"; // Reuse the god-level styles

const STATUS_CONFIG = {
  "2": { label: "Active", color: "success" },
  "1": { label: "Inactive", color: "default" },
  "0": { label: "Deleted", color: "error" },
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "-";
  const start = new Date(startDate);
  const end = new Date(endDate);
  const opts = { month: "short", day: "numeric", year: "numeric" };
  return `${start.toLocaleDateString("en-US", opts)} - ${end.toLocaleDateString("en-US", opts)}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatBudget = (value) => {
  if (value == null || value === "") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const truncate = (str, maxLen = 50) => {
  if (!str) return "-";
  return str.length <= maxLen ? str : str.slice(0, maxLen) + "...";
};

const WheelList = () => {
  const navigate = useNavigate();
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme") || "{}")?.theme?.color_shades || { 700: "#3b71ca" };

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({ status: "" });

  const [getWheelCampaignList, { data: listData, isLoading: listLoading, error: listError }] = useGetWheelCampaignListMutation();
  const [getWheelByCampaignId] = useGetWheelByCampaignIdMutation();
  const [getWheelRewardsByWheelId] = useGetWheelRewardsByWheelIdMutation();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const stats = useMemo(() => {
    if (!campaigns.length) return { total: 0, active: 0, budget: 0 };
    return {
      total: totalRows,
      active: campaigns.filter(c => c.status === "2").length,
      budget: campaigns.reduce((acc, c) => acc + (parseFloat(c.budget) || 0), 0)
    };
  }, [campaigns, totalRows]);

  const handlePreview = async (row) => {
    setIsPreviewLoading(true);
    setIsPreviewOpen(true);
    try {
      const wheelRes = await getWheelByCampaignId({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id,
        id: row.id
      }).unwrap();

      if (wheelRes?.body?.[0]?.id) {
        const rewardsRes = await getWheelRewardsByWheelId({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id,
          id: wheelRes.body[0].id
        }).unwrap();

        if (rewardsRes?.body) {
          setPreviewData(rewardsRes.body.map(r => ({ option: r.name || "Reward" })));
        }
      }
    } catch (err) {
      toast.error("Could not load preview data");
      console.error(err);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Debounce search (500ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
      
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchCampaigns = () => {
    getWheelCampaignList({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData") || "{}")?.tenant_id,
      page: currentPage,
      limit,
      status: filters.status || undefined,
      search: debouncedSearch || undefined,
    });
  };

  useEffect(() => {
    fetchCampaigns();
  }, [currentPage, limit, debouncedSearch, filters.status]);

  useEffect(() => {
    if (listError) {
      toast.error("Failed to load wheel campaigns");
      return;
    }
    if (!listData) return;
    // Support PDF response: data.campaigns + data.pagination
    const dataPayload = listData?.data;
    const bodyPayload = listData?.body;
    if (dataPayload?.campaigns) {
      setCampaigns(dataPayload.campaigns);
      const pag = dataPayload.pagination;
      if (pag) {
        setTotalRows(pag.total ?? 0);
        setTotalPages((pag.totalPages ?? Math.ceil((pag.total || 0) / (pag.limit || limit))) || 1);
      }
      return;
    }
    if (bodyPayload?.wheelCampaigns) {
      setCampaigns(bodyPayload.wheelCampaigns);
      const total = bodyPayload.totalWheelCampaigns ?? bodyPayload.wheelCampaigns?.length ?? 0;
      setTotalRows(total);
      setTotalPages(Math.ceil(total / limit) || 1);
    }
  }, [listData, listError, limit]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setOffset((pageNumber - 1) * limit);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setLimit(newPerPage);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleEdit = (row) => {
    navigate(`/genefied/${getVendorName()}/wheel`, { state: row });
  };

  const handleAddNew = () => {
    navigate(`/genefied/${getVendorName()}/wheelCampaign/add`);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ status: "" });
    setSearch("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: "ID",
        selector: (row) => row.id,
        sortable: true,
        width: "80px",
      },
      {
        name: "Campaign Title",
        cell: (row) => <span className="font-bold">{row.title || "-"}</span>,
        sortable: true,
        width: "160px",
      },
      {
        name: "Description",
        cell: (row) => truncate(row.description, 50),
        sortable: true,
      },
      {
        name: "Duration",
        cell: (row) => formatDateRange(row.start_date, row.end_date),
        sortable: true,
        width: "200px",
      },
      {
        name: "Status",
        cell: (row) => {
          const config = STATUS_CONFIG[row.status] || STATUS_CONFIG["1"];
          const isActive = config.color === "success";
          return (
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
                }`}
            >
              {config.label}
            </span>
          );
        },
        sortable: true,
        width: "100px",
      },
      {
        name: "Budget",
        cell: (row) => formatBudget(row.budget),
        sortable: true,
        width: "130px",
      },
      {
        name: "Created On",
        cell: (row) => formatDate(row.created_on),
        sortable: true,
        width: "120px",
      },
      {
        name: "Actions",
        cell: (row) => (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handlePreview(row)}
              className="flex justify-center items-center rounded-lg bg-indigo-500 p-2 text-white shadow-md hover:bg-indigo-600 transition-all hover:scale-105"
              title="Preview Wheel"
            >
              <FaEye />
            </button>
            <button
              type="button"
              onClick={() => handleEdit(row)}
              className="flex justify-center items-center rounded-lg bg-primary p-2 text-white shadow-md hover:bg-primary-600 transition-all hover:scale-105"
              title="Edit Campaign"
            >
              <AiOutlineEdit />
            </button>
          </div>
        ),
        width: "120px",
      },
    ],
    []
  );

  return (
    <>
      <div className="flex w-full bg-gray-100 min-h-screen justify-center">
        <div className="w-full text-left">
          <div className="god-header text-left">
            <div className="flex justify-between items-center max-w-7xl mx-auto">
              <div>
                <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
                  <FaChartLine className="text-indigo-200" />
                  Wheel Campaign Dashboard
                </h1>
                <p className="text-white/80 text-sm">Manage and monitor your spin-to-win campaigns</p>
              </div>
              <div className="flex items-center gap-4">
                <LinksComponent />
                <button
                  type="button"
                  onClick={handleAddNew}
                  className="inline-flex items-center rounded-xl px-5 py-2.5 text-sm font-bold text-indigo-600 bg-white shadow-xl hover:bg-indigo-50 transition-all hover:scale-105"
                >
                  <HiOutlinePlus className="mr-2 w-5 h-5 font-bold" />
                  New Campaign
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 max-w-7xl mx-auto -mt-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="glass-panel p-6 flex items-center gap-4 hover:translate-y-[-2px] transition-all">
                <div className="p-4 bg-indigo-100 text-indigo-600 rounded-2xl">
                  <FaListAlt size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Campaigns</p>
                  <h3 className="text-2xl font-bold">{stats.total}</h3>
                </div>
              </div>
              <div className="glass-panel p-6 flex items-center gap-4 hover:translate-y-[-2px] transition-all">
                <div className="p-4 bg-green-100 text-green-600 rounded-2xl">
                  <FaCheckCircle size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm font-medium">Active Now</p>
                  <h3 className="text-2xl font-bold">{stats.active}</h3>
                </div>
              </div>
              <div className="glass-panel p-6 flex items-center gap-4 hover:translate-y-[-2px] transition-all">
                <div className="p-4 bg-purple-100 text-purple-600 rounded-2xl">
                  <FaWallet size={24} />
                </div>
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Budget</p>
                  <h3 className="text-2xl font-bold">₹{stats.budget.toLocaleString()}</h3>
                </div>
              </div>
            </div>

            <div className="glass-panel p-6 bg-white shadow-2xl">
              {/* Filters and search */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search title or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="block w-64 rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm shadow-sm focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm shadow-sm focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  <option value="">All status</option>
                  <option value="2">Active</option>
                  <option value="1">Inactive</option>
                </select>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="py-2 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear filters
                </button>
              </div>

              {listLoading && <CircleLoaderComponent />}
              <DataTableComponent
                keyField="id"
                columns={columns}
                data={campaigns}
                loading={listLoading}
                totalRows={totalRows}
                paginationPerPage={limit}
                handlePerRowsChange={handlePerRowsChange}
                handlePageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={isPreviewOpen}>
          <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl p-8 shadow-2xl focus:outline-none flex flex-col items-center">
            <div className="flex justify-between w-full mb-6 items-center">
              <h2 className="text-2xl font-bold text-gray-800">Live Wheel Preview</h2>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-all"
              >
                <FaTimes className="text-gray-500" />
              </button>
            </div>

            {isPreviewLoading ? (
              <div className="h-64 flex items-center justify-center">
                <CircleLoaderComponent />
              </div>
            ) : (
              <div className="bg-gray-50 p-10 rounded-3xl shadow-inner border border-gray-100">
                <Wheels
                  data={previewData.length > 0 ? previewData : [{ option: "No Segments" }]}
                  backgroundColor={["#6366f1", "#a855f7", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"]}
                  textColors={["#ffffff"]}
                  outerBorderColor="#e2e8f0"
                  fontSize={14}
                />
              </div>
            )}

            <p className="mt-6 text-gray-400 text-sm">Previewing segments configured for this campaign</p>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export const wheelList = WheelList;
export default WheelList;
